import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import * as Sentry from '@sentry/browser'
import queryString from 'query-string'
import { useEffect, useLayoutEffect, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { generatePath, Navigate, useLocation, useNavigate } from 'react-router-dom-v5-compat'
import { isManagementPath } from 'shared/administration/routing'
import { OnboardingProgressProvider } from 'shared/components/Context/OnboardingProgressContext'
import { DrawerProvider } from 'shared/components/Drawer/DrawerContext'
import { Spinner } from 'shared/components/Spinner'
import { loadAzureMapsApis } from 'shared/helpers/loadAzureMapApis'
import { loadGoogleMapApis } from 'shared/helpers/loadGoogleMapApis'

import { Layout } from 'components/Layout/Layout'
import { AppContainer } from './AppContainer'
import { AppUserProvider } from './components/Context/AppUserProvider'
import { LayoutProvider } from './components/Context/LayoutContext'
import { REGISTER_INTENDED_PRODUCT_KEY } from './helpers/constants'
import { initAnalytics } from './helpers/initAnalytics'
import { useOnboardingChecklist } from './hooks/query/useOnboardingChecklist'
import { LOG_IN_REDIRECT } from './pages/Registration/VerifyCode/VerifyCode'
import { getSettings, setAzureMapsApiLoaded, setGoogleApisLoaded } from './redux/actions/settings/actions'
import { getUserAndTeammateInfo } from './redux/actions/users/actions'
import { currentTeamSelector } from './redux/selectors/team'
import { currentUserSelector, isVendorUserSelector } from './redux/selectors/users'
import { ApplicationState, ReduxDispatch } from './redux/store/models'
import { store } from './redux/store/store'
import { JOBS_PATH, REGISTRATION_PATH } from './Routes'

const initAnalyticsCalls = new Set<string>()

export const App: React.FC = ({ children }: { children?: React.ReactNode }) => {
  const dispatch: ReduxDispatch = useDispatch()
  const navigate = useNavigate()
  const isUserDataRefreshed = useRef(false)
  /** Initial `settings` is `{ azure_maps_api_key: '' }` — lodash `isEmpty` is false, so we still fetch `/api/v1/settings/` once after login or keys like `web_google_api_key` never arrive. */
  const appSettingsBootstrapRequested = useRef(false)

  //  flag for nav, bookmarks and job list changes
  const isAuthenticated = useSelector((state: ApplicationState) => state.auth.isAuthenticated)
  const azureMapsApiKey = useSelector(
    (state: ApplicationState) => state.settings.azure_maps_api_key as string
  )
  const webGoogleApiKey = useSelector(
    (state: ApplicationState) => state.settings.web_google_api_key as string | undefined
  )
  const googleApisLoaded = useSelector((state: ApplicationState) => state.settings.googleApisLoaded)
  const isVendorUser = useSelector(isVendorUserSelector)
  const currentUser = useSelector(currentUserSelector)
  const currentTeam = useSelector(currentTeamSelector)
  const location = useLocation()
  const isPublicPath =
    /^\/(?:registration|mobile-auth|job-tracking)(?:\/|$)/.test(location.pathname) ||
    location.pathname.replace(/\/$/, '') === '/login'
  const shouldFetchChecklist =
    isAuthenticated &&
    !isPublicPath &&
    Boolean(currentTeam?.product_subscription?.product_name) &&
    Boolean(currentUser.id) &&
    !currentUser.can_operate_fleet &&
    !currentUser.is_staff &&
    !isVendorUser

  const { allChecklistItems, isLoading: isChecklistLoading } = useOnboardingChecklist(currentUser.id, {
    enabled: shouldFetchChecklist,
  })

  useLayoutEffect(() => {
    if (isAuthenticated && !isPublicPath && (!isUserDataRefreshed.current || !currentUser.id)) {
      isUserDataRefreshed.current = true
      dispatch(getUserAndTeammateInfo()).then((data) => {
        if (data.vendor_membership || data.can_operate_fleet || data.is_staff) return

        if (!initAnalyticsCalls.has(data?.id)) {
          initAnalytics(data, data.team_object)
          initAnalyticsCalls.add(data?.id)
        }

        if (!data.team_object?.id) {
          const intendedProduct = localStorage?.getItem?.(REGISTER_INTENDED_PRODUCT_KEY)
          const redirectParam = (queryString.parse(window.location.search).redirectUrl as string) || ''

          if (redirectParam.startsWith('/registration')) {
            localStorage?.removeItem?.(REGISTER_INTENDED_PRODUCT_KEY)
            navigate(redirectParam)
          }

          navigate(
            intendedProduct ? generatePath(REGISTRATION_PATH, { bundle: intendedProduct }) : '/registration'
          )
        }
      })
    }
  }, [isAuthenticated, isPublicPath, dispatch, currentUser.id, navigate])

  useEffect(() => {
    if (currentUser?.id) {
      Sentry.configureScope((scope) => {
        scope.setUser({
          id: currentUser.id,
          team_id: currentTeam?.id,
          is_team_admin: currentUser.is_team_admin,
          is_team_driver: currentUser.is_team_driver,
        })
      })

      const redirectParam = (queryString.parse(window.location.search).redirectUrl as string) || ''
      const noRedirect = !redirectParam || redirectParam === '/'
      if (isAuthenticated && noRedirect && localStorage.getItem(LOG_IN_REDIRECT) === '1') {
        navigate(
          currentUser.is_staff
            ? '/manage/organizations'
            : currentUser.can_operate_fleet
              ? '/manage/company/'
              : JOBS_PATH
        )
      }
      localStorage.removeItem(LOG_IN_REDIRECT)
    }
  }, [
    isAuthenticated,
    currentUser?.id,
    currentUser?.organization,
    currentUser?.can_operate_fleet,
    currentUser?.is_staff,
    currentUser?.is_org_admin,
    currentUser?.company_setup_completed_at,
    currentUser?.team,
    currentUser?.is_team_admin,
    currentUser?.is_team_driver,
    currentTeam?.id,
    navigate,
  ])

  useEffect(() => {
    if (!isAuthenticated) {
      appSettingsBootstrapRequested.current = false
    }

    if (!isAuthenticated && !isPublicPath) {
      return
    }

    if (isAuthenticated && !appSettingsBootstrapRequested.current) {
      appSettingsBootstrapRequested.current = true
      dispatch(getSettings())
    }

    if (azureMapsApiKey) {
      loadAzureMapsApis(azureMapsApiKey, () => {
        dispatch(setAzureMapsApiLoaded())
      })
    }
    if (webGoogleApiKey) {
      loadGoogleMapApis(
        webGoogleApiKey,
        () => {
          if (store.getState().settings.googleApisLoaded) return
          dispatch(setGoogleApisLoaded())
        },
        Boolean(googleApisLoaded)
      )
    }
  }, [dispatch, azureMapsApiKey, webGoogleApiKey, googleApisLoaded, isAuthenticated, isPublicPath])

  const isTeamLoading =
    !currentUser.is_staff &&
    !currentUser.vendor_membership &&
    (currentUser.team ? currentTeam.id !== currentUser.team : !currentTeam.id && !currentUser.organization)

  if (isAuthenticated && !isPublicPath && (!currentUser.id || isTeamLoading)) {
    return <Spinner open={true} />
  }

  if (isChecklistLoading) {
    return <Spinner open={true} />
  }

  if (isAuthenticated && currentUser.is_staff && !isPublicPath && !isManagementPath(location.pathname)) {
    return <Navigate to="/manage/organizations" replace />
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <AppUserProvider>
        <LayoutProvider>
          <OnboardingProgressProvider>
            <Layout checklist={allChecklistItems}>
              <DrawerProvider>
                <AppContainer>{children}</AppContainer>
              </DrawerProvider>
            </Layout>
          </OnboardingProgressProvider>
        </LayoutProvider>
      </AppUserProvider>
    </LocalizationProvider>
  )
}
